import React, { useEffect, useState, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { isEmptyOrBlank, agentTimezoneMapping } from "utils/utils";
import { createAgent } from "./model";
import { emailFormatValidator, isAllInputValid } from "utils/formValidation";
import s from "./AddAgent.module.scss";
import Selection from "components/Input/Selection/Selection";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import RoleIdSearchField from "components/RoleIdSearchField/RoleIdSearchField";

import Spin from "components/Spin/Spin";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Divider from "components/Divider/Divider";
import ButtonMAS from "ButtonMAS/ButtonMAS";

import EnabledOptions from "utils/commonOptions/EnabledOptions";
import { getRoleOptions } from "../agentUtils";
import AgentStatusOptions from "utils/AgentStatusOptions/AgentStatusOptions";
import responseCodeMapping from "utils/response_code_mapping.json";

import merchantManagement from "routes/merchantManagement";
import { Link, Typography } from "@mui/material";
import useColors from "utils/useColors/useColors";
import useConfig from "utils/useConfig/useConfig";
import { useAgentOptionsCache } from "utils/optionsCache/agentOptionsCache";
import { usePayfacOptionsCache } from "utils/optionsCache/payfacOptionsCache";

const PAGE_LOCALE_KEY = "page.agents.agents_detail.";

const AGENTDETAIL_FIELD_INIT_STATE = {
  isoAgentId: "",
  payfacPrefix: "",
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  isOTPenable: "0",
  timezone: "Etc/GMT+8",
  permission: ["PORTAL"],
  status: "ACTIVE",
  roleId: "",
};

const ERROR_INIT_STATE = {
  isoAgentId: false,
  payfacPrefix: false,
  firstName: false,
  lastName: false,
  email: false,
  role: false,
  isOTPenable: false,
  timezone: false,
  permission: false,
  status: false,
  roleId: false,
};

const AddAgent = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const c = useColors();
  const { config } = useConfig();
  const { user, logout } = useAuth();
  const { invalidateAgentOptionsCache } = useAgentOptionsCache();
  const { fetchPayfacOptions } = usePayfacOptionsCache();
  const { enqueueSnackbar } = useSnackbar();
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };
  const gridId = "AgentMerchantsListGrid";

  const [agentDetailFieldInput, setAgentDetailFieldInput] = useState(
    AGENTDETAIL_FIELD_INIT_STATE,
  );
  const [selectedPayfac, setSelectedPayfac] = useState(null);
  const [payfacOptions, setPayfacOptions] = useState([]);
  const [hasMorePayfacs, setHasMorePayfacs] = useState(true);

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  const [submitting, setSubmitting] = useState(false);

  const roleOptions = getRoleOptions(user?.role);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const buildPayfacOption = (payfac) => {
    const id = payfac?.id ?? payfac?.payfacId ?? "";
    const payfacPrefix = payfac?.payfacPrefix ?? "";
    const name = payfac?.name ?? "";
    return {
      key: id,
      value: payfacPrefix,
      title: [id, payfacPrefix, name].filter(Boolean).join(" - "),
    };
  };

  const setPayfacSelection = (option, allowMore) => {
    setSelectedPayfac(option);
    setPayfacOptions(option ? [option] : []);
    setHasMorePayfacs(allowMore);
    setAgentDetailFieldInput((state) => ({
      ...state,
      payfacPrefix: option?.value || "",
    }));
  };

  useEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    const incomingState = sessionState && sessionState[path];
    const incomingPayfacPrefix = incomingState?.payfacPrefix;

    if (incomingPayfacPrefix) {
      setPayfacSelection(
        buildPayfacOption({
          id: incomingState?.payfacId,
          payfacPrefix: incomingPayfacPrefix,
          name: incomingState?.name,
        }),
        isSuperAdmin,
      );
    }
  }, [path, isSuperAdmin]);

  // ===================== Detail field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    setError({ ...error, [field]: false });
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setAgentDetailFieldInput({ ...agentDetailFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (callback) {
      if (agentDetailFieldInput[field]) {
        const isValidValue = callback(agentDetailFieldInput[field]);
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      } else {
        const isValidValue = callback("");
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      }
    }
    setFocus({ [field]: false });
  };

  const fetchPayfacSelectionOptions = useCallback(
    async (inputValue) => {
      const trimmed = (inputValue || "").trim();
      if (selectedPayfac?.value && trimmed === String(selectedPayfac.value)) {
        return payfacOptions;
      }
      const { options: cachedOptions, hasMoreOptions: nextHasMorePayfacs } =
        await fetchPayfacOptions(trimmed);
      const options = cachedOptions.map((option) => ({
        key: option.key,
        value: option.value,
        title: option.title,
      }));
      const selected =
        selectedPayfac && selectedPayfac.value ? selectedPayfac : null;
      const mergedOptions = selected
        ? [
          ...options,
          ...(options.some((opt) => opt.value === selected.value)
            ? []
            : [selected]),
        ]
        : options;
      setHasMorePayfacs(nextHasMorePayfacs);
      if (!trimmed) {
        setPayfacOptions(mergedOptions);
      }
      return mergedOptions;
    },
    [fetchPayfacOptions, payfacOptions, selectedPayfac],
  );

  const handlePayfacChange = (event, newValue) => {
    setError((state) => ({ ...state, payfacPrefix: false }));
    setPayfacSelection(newValue, true);
  };

  useEffect(() => {
    if (isSuperAdmin && payfacOptions.length === 0) {
      fetchPayfacSelectionOptions("");
    }
  }, [fetchPayfacSelectionOptions, isSuperAdmin, payfacOptions.length]);

  // ===================== detail field function end ===================== //

  // ===================== detail function  ===================== //

  const canAssignRole = ["SUPER_ADMIN", "PF_ADMIN"].includes(user?.role);

  const sendDetailUpdate = async () => {
    const requiredField = ["firstName", "lastName", "email"];
    if (isSuperAdmin) {
      requiredField.push("payfacPrefix");
    }
    if (canAssignRole) {
      requiredField.push("roleId");
    }
    let hasError = false;
    requiredField.forEach((field) => {
      const fieldValue = agentDetailFieldInput[field];
      if (isEmptyOrBlank(fieldValue)) {
        setError((error) => ({ ...error, [field]: true }));
        hasError = true;
      }
    });

    const allValid = isAllInputValid(
      error.isoAgentId,
      isSuperAdmin ? error.payfacPrefix : false,
      error.firstName,
      error.lastName,
      error.email,
      error.role,
      error.isOTPenable,
      error.timezone,
      error.permission,
      canAssignRole ? error.roleId : false,
      error.status,
      hasError,
    );

    if (!allValid) {
      return;
    }

    setSubmitting(true);
    const payload = { ...agentDetailFieldInput };
    payload.permission = JSON.stringify(agentDetailFieldInput.permission);
    payload.isOTPenable = agentDetailFieldInput.isOTPenable != "0"; // cast isOTPenable to boolean
    payload.timezone = agentTimezoneMapping(agentDetailFieldInput.timezone); // MAS-349. This function is aligned OBS & OP timezones behaviour when creating and rendering agent detail.

    const apiResponse = await createAgent(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            snackbar(
              t(PAGE_LOCALE_KEY + "create.msgFailed") +
              responseCodeMapping[responseBody.responseCode ?? "error"]["en"],
              "error",
            );
          } else {
            invalidateAgentOptionsCache();
            snackbar(t(PAGE_LOCALE_KEY + "create.msgSuccess"), "success");
            const newpath = "/merchant-management/agents/agents";
            const redirectpayload = {
              name: merchantManagement
                .find((tab) => tab.tabName === "Agents")
                .page.find((page) => page.path === newpath).name,
              path: newpath,
              refresh: true,
            };

            //order of execution is important, close -> redirect
            redirectFn("close", { tabId: path });
            redirectFn("redirectPage", redirectpayload);
          }
        }
      }
    }
    setSubmitting(false);
  };

  const AgentDetailsMainContent = () => {
    return (
      <div className={s.contentContainer}>
        {submitting && <Spin type={"search"} />}
        <div className={s.content2}>
          <div className={s.itemContainer}>
            <Grid gridId={gridId}>
              {isSuperAdmin && (
                <AsyncAutocomplete
                  label={t("common.payfac")}
                  required={isSuperAdmin}
                  fetchOptions={fetchPayfacSelectionOptions}
                  preloadedOptions={payfacOptions}
                  useLocalFilter
                  hasMoreOptions={hasMorePayfacs}
                  value={selectedPayfac}
                  onChange={handlePayfacChange}
                  getOptionLabel={(option) =>
                    option.title || option.label || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;

                    return (
                      <li key={key} {...optionProps}>
                        {option.title || option.label || ""}
                      </li>
                    );
                  }}
                  error={!!error.payfacPrefix}
                />
              )}
              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.firstName.label")}
                value={agentDetailFieldInput.firstName}
                onchangeFn={(e) => textInputOnChange(e, "firstName")}
                onfocusFn={() => textInputOnFocus("firstName")}
                onblurFn={() =>
                  textInputOnBlur("firstName", (val) => !isEmptyOrBlank(val))
                }
                sxParams={{
                  textFocus: focus.firstName,
                  textError: error.firstName,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.firstName.errorInput",
                )}
              />
              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.lastName.label")}
                value={agentDetailFieldInput.lastName}
                onchangeFn={(e) => textInputOnChange(e, "lastName")}
                onfocusFn={() => textInputOnFocus("lastName")}
                onblurFn={() =>
                  textInputOnBlur("lastName", (val) => !isEmptyOrBlank(val))
                }
                sxParams={{
                  textFocus: focus.lastName,
                  textError: error.lastName,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.lastName.errorInput",
                )}
              />

              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.email.label")}
                value={agentDetailFieldInput.email}
                onchangeFn={(e) => textInputOnChange(e, "email")}
                onfocusFn={() => textInputOnFocus("email")}
                onblurFn={() => textInputOnBlur("email", emailFormatValidator)}
                sxParams={{ textFocus: focus.email, textError: error.email }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.email.errorInput2",
                )}
              />
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.isOTPenable.label")}
                selectionValue={agentDetailFieldInput.isOTPenable}
                onchangeFn={(e) => textInputOnChange(e, "isOTPenable")}
                onfocusFn={() => textInputOnFocus("isOTPenable")}
                onblurFn={() => textInputOnBlur("isOTPenable")}
                options={EnabledOptions("all")}
                sxParams={{
                  textFocus: focus.isOTPenable,
                  textError: error.isOTPenable,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.isOTPenable.errorInput",
                )}
              />
            </Grid>

            <Grid gridId={gridId}>
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.role.label")}
                selectionValue={agentDetailFieldInput.role}
                onchangeFn={(e) => textInputOnChange(e, "role")}
                onfocusFn={() => textInputOnFocus("role")}
                onblurFn={() => textInputOnBlur("role")}
                options={roleOptions}
                sxParams={{ textFocus: focus.role, textError: error.role }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.role.errorInput",
                )}
              />

              {canAssignRole && (
                <RoleIdSearchField
                  label={t(
                    PAGE_LOCALE_KEY + "detailColumns.rolePermission.label",
                  )}
                  value={agentDetailFieldInput.roleId}
                  onChangeValue={(value) => {
                    setError((state) => ({ ...state, roleId: false }));
                    setAgentDetailFieldInput((state) => ({
                      ...state,
                      roleId: value,
                    }));
                  }}
                  currentUserRole={user?.role}
                  required
                  error={!!error.roleId}
                  errorMessage={t(
                    PAGE_LOCALE_KEY + "detailColumns.rolePermission.errorInput",
                  )}
                />
              )}
            </Grid>
            <Grid gridId={gridId}>
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.status.label")}
                selectionValue={agentDetailFieldInput.status}
                onchangeFn={(e) => textInputOnChange(e, "status")}
                onfocusFn={() => textInputOnFocus("status")}
                onblurFn={() => textInputOnBlur("status")}
                options={AgentStatusOptions(["ACTIVE", "SUSPENDED"])}
                sxParams={{ textFocus: focus.status, textError: error.status }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.status.errorInput",
                )}
              />
            </Grid>
          </div>
        </div>
        <div className={s.dialogActionContainer}>
          <Typography variant="content">
            <Trans
              i18nKey={PAGE_LOCALE_KEY + "disclaimer"}
              components={{
                ToU: <Link
                  color={c["brand1"]}
                  underline="none"
                  target="_blank"
                  href={config.TERMS_OF_USE}
                >
                  {t("layout.footer.terms")}
                </Link>,
                PP: <Link
                  color={c["brand1"]}
                  underline="none"
                  target="_blank"
                  href={config.PRIVACY_POLICY}
                >
                  {t("layout.footer.privacy")}
                </Link>
              }}
            />
          </Typography>
          <div className={s.dialogButtonContainer}>
            <ButtonMAS overlayLoading
              disabled={submitting}
              loading={submitting}
              onClick={sendDetailUpdate}
            >
              {t(PAGE_LOCALE_KEY + "btnCreate")}
            </ButtonMAS>
          </div>
        </div>
      </div>
    );
  };

  // ===================== detail function end ===================== //

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <div className={s.detailsContainer}>
          <div className={s.agentDetails}>
            <div className={s.header}>
              <span>{t(PAGE_LOCALE_KEY + "sectionCreateTitle")}</span>
            </div>

            <Divider />
            {AgentDetailsMainContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AddAgent);
